<template>
  <main
    class="user-page"
    role="main"
    aria-labelledby="page-title"
  >
    <div class="page-header">
      <h1
        id="page-title"
        class="h1"
      >
        Benutzer
      </h1>
      <div
        v-if="isAdmin"
        class="actions"
      >
        <button
          class="btn btn-primary"
          aria-label="Neu"
          title="Neu"
          data-testid="user-create"
          @click="openCreate"
        >
          Neu
        </button>
      </div>
    </div>

    <div class="card">
      <!-- Filterleiste -->
      <div class="filters">
        <input
          id="f-q"
          v-model="filters.q"
          type="search"
          placeholder="Name oder E‑Mail"
          autocomplete="off"
          aria-label="Suche"
          @input="onSearchInput"
        >

        <div
          class="field"
          style="min-width:180px;"
        >
          <label
            for="f-role"
            class="muted"
          >Rolle</label>
          <select
            id="f-role"
            v-model.number="filters.roleId"
            aria-label="Rolle"
            @change="onSelectChange"
          >
            <option :value="null">
              Alle
            </option>
            <option
              v-for="r in roles"
              :key="r.id"
              :value="String(r.id)"
            >
              {{ roleName(r.id) }}
            </option>
          </select>
        </div>

        <div
          class="field"
          style="min-width:160px;"
        >
          <label
            for="f-status"
            class="muted"
          >Status</label>
          <select
            id="f-status"
            v-model="filters.status"
            aria-label="Status"
            @change="onSelectChange"
          >
            <option :value="null">
              Alle
            </option>
            <option :value="true">
              Aktiv
            </option>
            <option :value="false">
              Inaktiv
            </option>
          </select>
        </div>

        <div style="display:flex; gap:8px;">
          <button
            class="btn btn-primary"
            aria-label="Filter zurücksetzen"
            title="Filter zurücksetzen"
            @click="resetFilters"
          >
            Reset
          </button>
        </div>
      </div>

      <div class="list">
        <div
          class="list-header muted"
          style="display:grid;grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr;gap:8px;padding:6px 0;border-bottom:1px solid var(--border);font-size:12px;line-height:16px;font-weight:600;"
        >
          <div
            class="sortable"
            role="button"
            tabindex="0"
            @click="setSort('name')"
            @keydown.enter="setSort('name')"
          >
            Name
            <span v-if="sortKey === 'name'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div
            class="sortable"
            role="button"
            tabindex="0"
            style="text-align:left;"
            @click="setSort('email')"
            @keydown.enter="setSort('email')"
          >
            E-Mail
            <span v-if="sortKey === 'email'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div
            class="sortable"
            role="button"
            tabindex="0"
            style="text-align:center;"
            @click="setSort('role')"
            @keydown.enter="setSort('role')"
          >
            Rolle
            <span v-if="sortKey === 'role'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </div>
          <div style="text-align:center;">
            Status
          </div>
          <div style="text-align:center;">
            Schüler
          </div>
          <div
            v-if="isAdmin"
            style="text-align:right;"
          >
            Aktionen
          </div>
        </div>
        <div
          v-for="u in sortedUsers"
          :key="u.id"
          class="table-row"
        >
          <div><strong>{{ formatName(u) }}</strong></div>
          <div style="text-align:left;">
            {{ u.email }}
          </div>
          <div style="text-align:center;">
            <span
              class="badge"
              :data-role="u.roleId"
            >{{ roleName(u.roleId) }}<span
              v-if="u.roleId===2 && u.hasExtraPermissions"
              class="ml-1"
            >+</span></span>
          </div>
          <div style="text-align:center;">
            {{ u.isActive ? 'Aktiv' : 'Inaktiv' }}
          </div>
          <div style="text-align:center;">
            {{ assignedCount(u) }}
          </div>
          <div
            v-if="isAdmin"
            class="row-actions"
            style="display:flex;gap:8px;justify-content:flex-end;"
          >
            <button
              v-if="isAdmin && isParentRole(u.roleId)"
              class="icon-btn"
              aria-label="Schüler zuweisen"
              title="Schüler zuweisen"
              @click="openAssign(u.id)"
            >
              <i
                class="pi pi-user"
                aria-hidden="true"
              />
            </button>
            <button
              class="icon-btn"
              aria-label="Bearbeiten"
              title="Bearbeiten"
              @click="openEdit(u.id)"
            >
              <i
                class="pi pi-pencil"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="isAdmin"
              class="icon-btn"
              :disabled="u.id === currentUserId"
              aria-label="Löschen"
              title="Löschen"
              @click="openDelete(u.id)"
            >
              <i
                class="pi pi-trash"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted"
          style="margin-right:auto"
        >Gesamt: {{ total }}</span>
        <label
          class="muted"
          for="gr-limit"
        >pro Seite:</label>
        <select
          id="gr-limit"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </div>

    <!-- Dialoge -->
    <UserCreateDialog
      v-if="isAdmin"
      :open="dlgCreate"
      :roles="roles"
      @close="dlgCreate=false"
      @saved="onSaved"
    />
    <UserEditDialog
      :id="currentId"
      :open="dlgEdit"
      :roles="roles"
      @close="closeEdit"
      @saved="onSaved"
    />
    <UserDeleteDialog
      v-if="isAdmin && dlgDelete"
      :id="currentId"
      :name="currentDisplayName"
      :open="dlgDelete"
      @close="closeDelete"
      @deleted="onSaved"
    />
    <ParentAssignDialog
      v-if="isAdmin"
      :open="dlgAssign"
      :user-id="assignUserId"
      @close="closeAssign"
      @changed="onSaved"
    />

    <!-- Top-Navigation: Hilfe-Button -->
    <teleport to=".top-nav">
      <span
        id="top-nav-help-users"
        class="top-nav-help"
      >
        <UiInfoButton title="Hilfe: Benutzerverwaltung">
          <ul>
            <li>Suche nach Name oder E‑Mail, Filter nach Rolle und Status.</li>
            <li>Spalten: Name, E‑Mail, Rolle, Status, ggf. Anzahl zugewiesener Schüler.</li>
            <li>Aktionen: Schüler zuweisen (nur Elternrolle), Bearbeiten, Löschen (eigener Account ist geschützt).</li>
            <li>Paginierung: Anzahl pro Seite wählen und navigieren.</li>
            <li>Rollenbezeichnungen stammen aus dem System; unbekannte Rollen werden neutral angezeigt.</li>
          </ul>
        </UiInfoButton>
      </span>
    </teleport>
  </main>
</template>

<script setup>
import {computed, onMounted, onBeforeUnmount, ref} from 'vue';
import usersApi from '@/api/users';
import UserCreateDialog from '@/components/dialogs/UserCreateDialog.vue';
import UserEditDialog from '@/components/dialogs/UserEditDialog.vue';
import UserDeleteDialog from '@/components/dialogs/UserDeleteDialog.vue';
import ParentAssignDialog from '@/components/dialogs/ParentAssignDialog.vue';
import UiInfoButton from '@/components/UiInfoButton.vue';

const user = JSON.parse(localStorage.getItem('user') || 'null');
const isAdmin = user?.roleId === 1;
const currentUserId = user?.id ?? null;
const users = ref([]);
const roles = ref([]);
const dlgCreate = ref(false);
const dlgEdit = ref(false);
const dlgDelete = ref(false);
const dlgAssign = ref(false);
const assignUserId = ref(null);
const currentId = ref(null);
const filters = ref({roleId: null, status: null, q: ''});
const searchTimer = ref(null);

// Pagination wie in Groups
const page = ref(1);
const limit = ref(20);
const pages = ref(1);
const total = ref(0);

function roleName(roleId) {
  const r = roles.value.find(r => r.id === roleId);
  if (!r) return String(roleId);
  // Bevorzugt Beschreibung aus DB (DE-Label); Fallback Mapping
  if (r.description && String(r.description).trim() !== '') return r.description;
  const map = {admin: 'Admin', teacher: 'Lehrer', parent: 'Eltern'};
  return map[r.name] || r.name;
}

function isParentRole(roleId) {
  const r = roles.value.find(r => r.id === roleId);
  return (r && r.name === 'parent') || roleId === 3;
}

function assignedCount(u) {
  // Nur für Eltern anzeigen, sonst '–'
  if (!isParentRole(u.roleId)) return '–';
  const c = u.assignedStudentsCount ?? 0;
  const n = Number(c);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function formatName(u) {
  const last = (u.lastName || '').trim();
  const first = (u.firstName || '').trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || (u.displayName || '').trim() || '';
}

// Sortierung
const sortKey = ref('name');
const sortDir = ref('asc');

const sortedUsers = computed(() => {
  const arr = [...users.value];
  const getVal = (u) => {
    switch (sortKey.value) {
      case 'email':
        return (u.email || '').toLowerCase();
      case 'role':
        return (roleName(u.roleId) || '').toLowerCase();
      case 'name':
      default:
        return (formatName(u) || '').toLowerCase();
    }
  };
  arr.sort((a, b) => {
    const va = getVal(a);
    const vb = getVal(b);
    const result = String(va).localeCompare(String(vb), 'de', {sensitivity: 'base'});
    return sortDir.value === 'asc' ? result : -result;
  });
  return arr;
});

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
}

async function load() {
  const params = {page: page.value, limit: limit.value};
  if (filters.value.roleId !== '' && filters.value.roleId !== null && filters.value.roleId !== undefined) {
    params.role = Number(filters.value.roleId);
  }
  if (filters.value.status != null) params.status = filters.value.status;
  if (filters.value.q) params.q = filters.value.q;

  try {
    const res = await usersApi.list(params);
    const data = res?.data ?? {};

    // Items robust extrahieren (unterstützt verschiedene Response-Formate)
    const items = Array.isArray(data)
      ? data
      : (Array.isArray(data.data)
          ? data.data
          : (Array.isArray(data.items)
              ? data.items
              : (Array.isArray(data.data?.items) ? data.data.items : [])));

    users.value = items ?? [];

    // total und pages priorisiert aus Server, sonst fallback
    const tot = (data?.total ?? data?.data?.total ?? users.value.length);
    total.value = Number(tot) || 0;

    const pgs = (data?.pages ?? data?.data?.pages ?? Math.max(1, Math.ceil(total.value / Number(limit.value || 1))));
    pages.value = Number(pgs) || 1;
  } catch (e) {
    users.value = [];
    total.value = 0;
    pages.value = 1;
  }
}

async function loadRoles() {
  const res = await usersApi.roles();
  roles.value = res.data?.data || res.data || [];
}

function onSearchInput() {
  if (searchTimer.value) {
    clearTimeout(searchTimer.value);
  }
  searchTimer.value = setTimeout(() => {
    page.value = 1;
    load();
    searchTimer.value = null;
  }, 300);
}

function resetFilters() {
  filters.value = {roleId: null, status: null, q: ''};
  page.value = 1;
  load();
}

function onSelectChange() {
  page.value = 1;
  load();
}

function prevPage() {
  if (page.value > 1) {
    page.value -= 1;
    load();
  }
}

function nextPage() {
  if (page.value < pages.value) {
    page.value += 1;
    load();
  }
}

function changeLimit(e) {
  const newLimit = Number(e?.target?.value ?? e);
  if (Number.isFinite(newLimit) && newLimit > 0) {
    limit.value = newLimit;
    page.value = 1;
    load();
  }
}

function openCreate() {
  dlgCreate.value = true;
}

function openEdit(id) {
  currentId.value = id;
  dlgEdit.value = true;
}

function closeEdit() {
  dlgEdit.value = false;
  currentId.value = null;
}

function openDelete(id) {
  currentId.value = id;
  dlgDelete.value = true;
}

function openAssign(id) {
  assignUserId.value = id;
  dlgAssign.value = true;
}

function closeAssign() {
  dlgAssign.value = false;
  assignUserId.value = null;
}

function closeDelete() {
  dlgDelete.value = false;
  currentId.value = null;
}

async function onSaved() {
  dlgCreate.value = false;
  closeEdit();
  dlgDelete.value = false;
  closeAssign();
  await load();
}

onMounted(async () => {
  await Promise.all([load(), loadRoles()]);
});

// Hilfe-Button vor .profile einsortieren
onMounted(() => {
  const nav = document.querySelector('.top-nav');
  const el = document.getElementById('top-nav-help-users');
  if (!nav || !el) return;
  const profile = nav.querySelector('.profile');
  if (profile && profile.parentNode === nav) nav.insertBefore(el, profile);
  else nav.appendChild(el);
});

onBeforeUnmount(() => {
  const el = document.getElementById('top-nav-help-users');
  if (el && el.parentNode) el.parentNode.removeChild(el);
});

const currentDisplayName = computed(() => {
  const id = currentId.value;
  if (!id) return '';
  const u = users.value.find(u => u.id === id);
  if (!u) return '';
  return formatName(u) || u.email || '';
});
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
}

.table-row {
  padding: 5px 0;
  font-size: 14px;
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr;
  gap: 8px;
  border-bottom: 1px solid var(--border);
}

.sortable:hover {
  text-decoration: underline;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(0, 0, 0, .06);
}

.badge[data-role="1"] {
  background: rgba(239, 68, 68, .1);
  color: #991b1b;
}

/* admin rot */
.badge[data-role="2"] {
  background: rgba(37, 99, 235, .1);
  color: #1e3a8a;
}

/* teacher blau */
.badge[data-role="3"] {
  background: rgba(16, 185, 129, .1);
  color: #065f46;
}

/* parent grün */
</style>
